import { Fragment } from 'react';
import PropTypes from 'prop-types';

import themePropTypes from 'core/utils/prop-types/theme';
import { resolveScopedStyles } from 'core/utils/styled-jsx';

import withBreakpoint from 'core/hocs/withBreakpoint';
import withTheme from 'core/hocs/withTheme';
import Link from 'core/components/Link';

import styles from './index.styl';


function ShapkaMenuItems(props) {
  const {
    items,
    isNavItem,
    currentItem,
    theme,
    menuInjection,
    isMobile,
    startHideIndex,
    renderers,
    isFirstShapkaForPremium,
  } = props;

  const injection = menuInjection || {};
  const shapkaAtoms = theme.controls.shapka;
  let linkAtoms = shapkaAtoms.dropdown.link;
  if (isFirstShapkaForPremium) {
    linkAtoms = shapkaAtoms.dropdown.premiumTopic.link;
  } else if (isNavItem) {
    linkAtoms = shapkaAtoms.link;
  }

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .${styles.dropdownItem}
          &:not(:last-of-type)
          &._isFirstShapkaForPremium
            border-bottom ${shapkaAtoms.menu.itemDivider}

        .${styles.link}
          font ${linkAtoms.font}
          padding ${linkAtoms.padding}
          text-transform ${linkAtoms.textTransform}
          letter-spacing ${linkAtoms.letterSpacing}
          color ${linkAtoms.idle.color}
          text-decoration ${linkAtoms.idle.decoration}
          transition color ${theme.animations.hover}

          &:active
            color ${linkAtoms.active.color}
            text-decoration ${linkAtoms.active.decoration}
          &:hover
            color ${linkAtoms.hover.color}
            text-decoration ${linkAtoms.hover.decoration}
        .${styles.link}.${styles._current}
          color ${linkAtoms.current.color}
          text-decoration ${linkAtoms.current.decoration}
      `}</style>
    </scope>
  );

  return (
    <Fragment>
      {items.map((item, i) => {
        const Injection = injection[i];
        const isCurrent = item.link === currentItem?.link;

        const linkWrapperClassName = scoped.wrapClassNames(
          shapkaAtoms.dropdown.fullwidth && !isNavItem && styles._fullWidthDropdownItem,
          isNavItem ? styles.navItem : styles.dropdownItem,
          i >= startHideIndex && styles._hidden,
          isFirstShapkaForPremium && '_isFirstShapkaForPremium',
        );

        const linkClassName = scoped.wrapClassNames(
          styles.link,
          isCurrent && styles._current,
          isMobile && shapkaAtoms.menu.centered && styles._linkCentered
        );

        const itemMarkup = (
          <Fragment key={item.link + i}>
            {Injection && <Injection />}
            <div
              className={linkWrapperClassName}
              data-qa='lb-menu-item'
            >
              <Link
                type='blank'
                to={item.link}
                className={linkClassName}
                data-qa='lb-menu-item-link'
              >
                {item.content}
              </Link>
            </div>
          </Fragment>
        );

        const itemRenderer = renderers?.[item.link];

        if (typeof itemRenderer === 'function') {
          return itemRenderer({
            item,
            index: i,
            isCurrent,
            coreMarkup: itemMarkup,
            linkWrapperClassName,
            linkClassName,
          });
        }

        return itemMarkup;
      })}
      <scoped.styles />
    </Fragment>
  );
}


const linkStateContract = `{
  color,
  decoration
}`;

const linkContract = `{
  font,
  textTransform,
  padding,
  letterSpacing,
  idle: ${linkStateContract},
  hover: ${linkStateContract},
  active: ${linkStateContract},
  current: ${linkStateContract},
}`;

ShapkaMenuItems.propTypes = {
  isFirstShapkaForPremium: PropTypes.bool,

  /** @ignore */
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },
      controls: {
        shapka: {
          link: ${linkContract},
          dropdown: {
            link: ${linkContract},
            fullwidth
          },
          menu: {
            itemDivider,
            centered
          }
        }
      }
    }
  `),
  /**
   * Справочник элементов в меню
   */
  items: PropTypes.array,

  /** Текущий элемент в шапке */
  currentItem: PropTypes.object,

  isNavItem: PropTypes.bool,
  isMobile: PropTypes.bool,
  /**
     * Справочник дополнительных элементов, подмешаных в меню
     *
     * В качестве ключа должен выступать индекс элемента меню, перед которым
     * должен встать дополнительный элемент
     * а в качестве значения компонент этого элемента
     */
  menuInjection: PropTypes.objectOf(PropTypes.func.isRequired),
  /** Скрывать пункты с индексом больше указаного */
  startHideIndex: PropTypes.number,
  /**
   * Коллекция кастомных рендереров
   */
  renderers: PropTypes.object,
};

const ShapkaMenuItemsComponent = withTheme(withBreakpoint(ShapkaMenuItems));
ShapkaMenuItemsComponent.displayName = 'ShapkaMenuItems';

export { ShapkaMenuItems as StorybookComponent };
export default ShapkaMenuItemsComponent;
